/**
 * Wire ↔ form-value conversions shared by the market variants of the Licenses step.
 *
 * Both markets read the same envelope (`HomeListingLicensesModel`) and post the same one back, so
 * the date and uploaded-file plumbing is identical; only the fields differ. Kept here rather than
 * imported across `dc/` and `mi/` so neither market owns the other's helpers — the NJ step already
 * carries its own copy of `parseDateMMDDYYYY`, and a third one is not worth having.
 */

/** The `NewMediaUploader` field value shape. */
export interface MediaFieldValue {
  availableMedia?: unknown[];
  mediaToDisplay?: unknown[];
  stagingMedia?: unknown[];
  deletedMedia?: unknown[];
}

/**
 * `utils/dateTimeUtils` only parses 2-digit years, so the MM/DD/YYYY inputs on this step need their
 * own parser.
 */
export function parseDateMMDDYYYY(value?: string): Date | null {
  if (!value) return null;
  const parts = value.split('/');
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (!month || !day || !year || String(year).length < 4) return null;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;
  return date;
}

/** MM/DD/YYYY input value → ISO string for the API. */
export function toIso(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const date = parseDateMMDDYYYY(value);
  return date ? date.toISOString() : undefined;
}

/** API date-time → MM/DD/YYYY for display. Read in UTC so the date doesn't shift a day west. */
export function toDisplayDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  const date = new Date(value);
  if (isNaN(date.getTime())) return undefined;
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${month}/${day}/${date.getUTCFullYear()}`;
}

/** An uploaded document from the API → the uploader field's value. */
export function toMediaValue(file: unknown): MediaFieldValue | undefined {
  if (!file || !(file as { uniqueId?: string }).uniqueId) return undefined;
  return {
    availableMedia: [file],
    mediaToDisplay: [file],
    stagingMedia: [],
    deletedMedia: [],
  };
}
